import Footer from "components/Footer/Footer";
import ScrollToTop from "components/ScrollToTop";
import React, { useState, useEffect } from "react";
import EmptyList from "../Common/EmptyList";
import BlogList from "./Home/BlogList";
import Header2 from "./Home/Header/Header2";
import SearchBar from "./Home/SearchBar";
import { collection, query, where, getDocs, orderBy, limit, startAfter, getCountFromServer } from "firebase/firestore";
import { db } from "../../useFirebase";

const ITEMS_PER_PAGE = 24;
const SEARCH_LIMIT = ITEMS_PER_PAGE;
const ROLES = ["All", "Software", "Core", "Finance", "Consultancy", "Data", "Product", "Quant", "FMCG"];

const Internblogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  // We keep a history of the *first* document of each page to paginate backwards,
  // or simply a history of "lastVisible" of previous pages to use as startAfter points.
  // Map page number -> startAfter doc. Page 1 -> null. Page 2 -> end of Page 1.
  const [cursorHistory, setCursorHistory] = useState({ 1: null });
  const [isLastPage, setIsLastPage] = useState(false);

  // Search Mode Toggle (derived from searchKey)
  // If searching, we fetch all relevant docs and filter client side.
  // If browsing, we paginate server side.
  const searchMode = !!searchKey;

  // 1. Fetch function for Server-Side Pagination (Browse Mode)
  const fetchPaginatedBlogs = async (page) => {
    try {
      setLoading(true);
      const now = new Date();
      const blogsRef = collection(db, "blogs_new");

      // Base constraints
      let constraints = [
        where("type", "==", "internship"),
        where("createdAt", "<=", now.toISOString()),
        // Sorting: Newest first
        orderBy("createdAt", "desc"),
      ];

      // Role Filter using IN operator to handle mixed casing in DB
      if (selectedRole !== "All") {
        const variations = [selectedRole];
        if (selectedRole === "Consultancy") {
          variations.push("consultancy", "Consult", "consult");
        } else {
          variations.push(selectedRole.toLowerCase(), selectedRole.toUpperCase());
        }
        const uniqueVars = Array.from(new Set(variations));
        constraints.push(where("category", "in", uniqueVars));
      }

      // Pagination Cursor
      const startCursor = cursorHistory[page];
      if (startCursor) {
        constraints.push(startAfter(startCursor));
      }

      // Limit
      constraints.push(limit(ITEMS_PER_PAGE));

      const q = query(blogsRef, ...constraints);
      const querySnapshot = await getDocs(q);

      const fetchedBlogs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter future posts (Client side verify for mixed formats)
      // If consistency is guaranteed, we could add .where to query.
      const getDate = (d) => (d?.toDate ? d.toDate() : new Date(d));
      const validBlogs = fetchedBlogs.filter((b) => getDate(b.createdAt) <= now);

      setBlogs(validBlogs);

      // Prepare cursor for next page
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      if (querySnapshot.docs.length < ITEMS_PER_PAGE) {
        setIsLastPage(true);
      } else {
        setIsLastPage(false);
        // Set cursor for NEXT page (page + 1)
        setCursorHistory((prev) => ({ ...prev, [page + 1]: lastDoc }));
      }
    } catch (error) {
      console.error("Error fetching paginated blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  // 2. Fetch function for Search Mode (Client-Side Filter)
  const fetchAllForSearch = async () => {
    try {
      setLoading(true);
      const trimmed = searchKey.trim();
      if (!trimmed) {
        setBlogs([]);
        return;
      }

      // Generate variations for case-insensitive prefix match
      const vars = new Set();
      vars.add(trimmed);
      vars.add(trimmed.toLowerCase());
      vars.add(trimmed.toUpperCase());
      
      const titleCase = trimmed
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
      vars.add(titleCase);

      const capCase = trimmed
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      vars.add(capCase);

      const variations = Array.from(vars);
      const promises = [];
      const blogsRef = collection(db, "blogs_new");

      for (const v of variations) {
        // Query company prefix
        const qCompany = query(
          blogsRef,
          where("company", ">=", v),
          where("company", "<=", v + "\uf8ff")
        );
        promises.push(getDocs(qCompany));

        // Query name_ prefix
        const qName = query(
          blogsRef,
          where("name_", ">=", v),
          where("name_", "<=", v + "\uf8ff")
        );
        promises.push(getDocs(qName));
      }

      const snapshots = await Promise.all(promises);
      const idMap = new Map();

      for (const snap of snapshots) {
        for (const doc of snap.docs) {
          idMap.set(doc.id, { id: doc.id, ...doc.data() });
        }
      }

      // Filter by type and role client-side
      let result = Array.from(idMap.values()).filter((doc) => doc.type === "internship");

      if (selectedRole !== "All") {
        const roleLower = selectedRole.toLowerCase();
        result = result.filter((blog) => {
          if (!blog.category) return false;
          const catLower = blog.category.toLowerCase();
          if (roleLower === "consultancy") {
            return catLower === "consultancy" || catLower === "consult";
          }
          return catLower === roleLower;
        });
      }

      // Sort by createdAt desc since we merged different queries
      result.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA;
      });

      setBlogs(result);
      setIsLastPage(true);
    } catch (error) {
      console.error("Error searching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect: Trigger Fetch on dependency change
  useEffect(() => {
    if (searchKey) {
      const delayDebounceFn = setTimeout(() => {
        fetchAllForSearch();
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } else {
      // Reset to page 1 when role changes or search is cleared
      // But only if we are not just navigating pages.
      // We'll handle page navigation separately.
      fetchPaginatedBlogs(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, selectedRole, currentPage]);

  // Handler for Role Change (Reset Paging)
  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setCurrentPage(1);
    setCursorHistory({ 1: null }); // Reset cursor history
    setIsLastPage(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    if (newPage > currentPage && isLastPage && !searchMode) return;

    setCurrentPage(newPage);
  };

  const handleClearSearch = () => {
    setSearchKey("");
    handleRoleChange("All"); // Reset everything
  };

  return (
    <div>
      <Header2 />

      {/* Controls */}
      <div
        style={{
          maxWidth: "800px",
          margin: "20px auto",
          padding: "0 20px",
          display: "flex",
          gap: "15px",
          flexDirection: "column",
          alignItems: "center",
        }}>
        <SearchBar
          value={searchKey}
          clearSearch={handleClearSearch}
          formSubmit={(e) => e.preventDefault()}
          handleSearchKey={(e) => setSearchKey(e.target.value)}
        />

        {/* Role Filter */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          <span style={{ fontWeight: "bold", color: "#333" }}>Filter by Role:</span>
          <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "center" }}>
            {ROLES.map((role) => (
              <button
                key={role}
                onClick={() => handleRoleChange(role)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "20px",
                  border: "1px solid #ddd",
                  background: selectedRole === role ? "#1a3c5e" : "#fff",
                  color: selectedRole === role ? "#fff" : "#333",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  transition: "all 0.2s",
                }}>
                {role}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ?
        <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
      : !blogs.length ?
        <EmptyList />
      : <BlogList blogs={blogs} />}

      {/* Pagination Controls */}
      {!loading && !searchMode && (
        <div
          style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", padding: "30px 0" }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: "8px 16px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.5 : 1,
            }}>
            Previous
          </button>
          <span>Page {currentPage}</span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={isLastPage}
            style={{
              padding: "8px 16px",
              cursor: isLastPage ? "not-allowed" : "pointer",
              opacity: isLastPage ? 0.5 : 1,
            }}>
            Next
          </button>
        </div>
      )}

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Internblogs;
